import { seoLinks, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import ogHeroImage from "../assets/preschool-play.webp";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import { GuideLinks } from "../components/site/Guides";
import { KnowledgeHubNote } from "../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../components/site/NewcomerNote";

const title = "Svensk förskola i Spanien – så fungerar den i praktiken";
const description =
  "Guide till svensk förskola utomlands: läroplanens idéer, dagsrytm, språkutveckling, inskolning och vad du bör fråga en förskola på Costa del Sol om.";

export const Route = createFileRoute("/svensk-forskola-i-spanien")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      ...ogImage("/og/forskola-v1.jpg"),
      { property: "og:locale", content: "sv_SE" },
    ],
    links: seoLinks("/svensk-forskola-i-spanien"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Svensk förskola i Spanien"
        intro="Vad innebär det egentligen att låta ett litet barn börja på en svensk förskola utomlands? Här går vi igenom hur vardagen ser ut, hur språken utvecklas parallellt och vilka frågor det är värt att ställa innan ni bestämmer er."
      />

      <NewcomerNote />

      <Prose>
        <h2>Samma pedagogiska grund som hemma</h2>
        <p>
          Svensk förskolepedagogik bygger på att barn lär sig genom lek, omsorg och utforskande
          tillsammans med andra – inte genom skolliknande lektioner. Barnet ses som kompetent och
          nyfiket, och de vuxnas uppgift är att skapa en trygg ram där barnet vågar prova, misslyckas
          och prova igen. Det gäller lika mycket i Spanien som i Sverige. Skillnaden ligger i
          omgivningen: klimatet gör att en mycket större del av dagen kan tillbringas utomhus, och
          barngruppen har ofta flera språk och nationaliteter i sig från början.
        </p>
        <p>
          För en familj som planerar att så småningom flytta hem igen är kontinuiteten det tyngsta
          argumentet. Barnet fortsätter i samma pedagogiska tradition, med svenska som gemensamt
          språk i gruppen, och övergången tillbaka till en svensk förskola eller förskoleklass blir
          sällan dramatisk. För familjer som planerar att stanna länge är argumentet snarare att
          barnet får med sig svenskan som ett levande vardagsspråk, inte bara som ett hemspråk.
        </p>

        <h2>En vanlig dag</h2>
        <p>
          Dagen har en förutsägbar rytm, vilket små barn mår bra av: lugn ankomst, samling, fri lek
          och styrda aktiviteter, mellanmål, utevistelse och vila. Aktiviteterna kretsar kring
          språk, skapande, rörelse, natur och enkel matematik i vardagen – räkna, sortera, jämföra.
          Vi arbetar mycket med sagor, sång och rim eftersom det bygger ordförråd och språkljud
          samtidigt som det är roligt.
        </p>
        <p>
          Utevistelsen är omfattande. Under vår, höst och vinter är det oftast behagligt att vara ute
          stora delar av dagen, medan de varmaste veckorna kräver skugga, vattenlek och anpassade
          tider. Solskydd, keps och extra kläder är en självklar del av packningen året om.
        </p>

        <h2>Tre språk utan press</h2>
        <p>
          Svenska är basen. Spanska och engelska kommer in genom sång, lek, rutinsituationer och
          återkommande aktiviteter snarare än genom formell undervisning. Små barn lär sig språk
          genom att höra dem användas i sammanhang som betyder något för dem – vid maten, i
          leken, i tamburen.
        </p>
        <p>
          Det är helt normalt att barn blandar språk under en period, eller att ett språk ligger
          tyst ett tag innan det plötsligt kommer. Vi jagar inte tidiga resultat. Målet är att
          barnet ska tycka att flera språk är något naturligt och odramatiskt, inte något man kan
          göra fel.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="sv" />
      </div>

      <Prose>

        <h2>Inskolning och trygghet</h2>
        <p>
          Inskolningen görs stegvis och i barnets takt, med en förälder nära till hands i början.
          Många familjer som just flyttat har mycket annat på gång samtidigt – bostad, myndigheter,
          jobb – och då är det extra viktigt att förskolan blir den del av tillvaron som känns stabil
          först. Vi bestämmer upplägget tillsammans med er utifrån barnets ålder och tidigare
          erfarenhet av förskola.
        </p>
        <p>
          Vi håller nära kontakt med vårdnadshavare i vardagen: korta avstämningar vid lämning och
          hämtning, och längre samtal när det behövs. Har barnet allergier, särskilda behov eller en
          historia som vi bör känna till, vill vi veta det från start.
        </p>

        <h2>Frågor att ställa till vilken förskola som helst</h2>
        <ul>
          <li>Vilken läroplan arbetar ni efter, och hur märks det i vardagen?</li>
          <li>Hur många barn går i gruppen och hur många vuxna arbetar där?</li>
          <li>Vilka språk talas mellan barn och personal under dagen?</li>
          <li>Hur ser inskolningen ut, och hur lång tid brukar den ta?</li>
          <li>Hur ser utemiljön ut, och hur hanteras värme och sol?</li>
          <li>Vad ingår i avgiften – mat, utflykter, material?</li>
          <li>Hur kommunicerar ni med vårdnadshavare i vardagen?</li>
        </ul>

        <h2>Hos oss</h2>
        <p>
          Vår förskola tar emot barn från tre år och ligger i samma trygga miljö som skolan, vilket
          gör steget vidare till förskoleklass litet. Läs mer om{" "}
          <Link className="text-primary link-quiet" to="/forskola">
            vår förskola
          </Link>
          , se{" "}
          <Link className="text-primary link-quiet" to="/avgifter">
            avgifterna
          </Link>{" "}
          eller hör av er så visar vi runt.
        </p>
      </Prose>

      <KnowledgeHubNote resource="flyttchecklistan" />

      <GuideLinks current="/svensk-forskola-i-spanien" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
